import React from 'react';
import { TrendingUp } from 'lucide-react';
import { fmtMoney } from './format';
import SaveSlotsBlock from '../../supervision/SaveSlotsBlock';

/**
 * DetailPanel — expanded row content showing per-apprentice telemetry
 * (activity, finances, human metrics, strategic analysis).
 * Extracted from GlobalSupervisionTab on 29/05/2026.
 */
export default function DetailPanel({ item }) {
  const s = item.snapshot;
  if (!s) {
    // 18/07/2026 — diagnostic enrichi grâce au heartbeat « app ouverte » :
    // on distingue « jamais ouvert » de « ouvert mais télémétrie muette ».
    const lastOpen = item.license?.last_app_open;
    if (lastOpen) {
      return (
        <div className="text-xs text-slate-400 space-y-1" data-testid="detail-no-snapshot-opened">
          <p className="text-amber-300 font-medium">
            A ouvert le jeu (dernière ouverture : {new Date(lastOpen).toLocaleString('fr-FR')}) mais aucune donnée de partie reçue.
          </p>
          <p className="text-slate-500 italic">
            Soit la société n&apos;a jamais été créée (la télémétrie de jeu démarre à la création),
            soit les envois sont bloqués (pare-feu / extension navigateur). Demandez à l&apos;apprenant
            d&apos;ouvrir « Sauvegarder » et de lire la ligne « Synchro supervision ».
          </p>
          {item.license?.last_app_open_ua && (
            <p className="text-[10px] text-slate-600 font-mono truncate" title={item.license.last_app_open_ua}>
              Navigateur : {item.license.last_app_open_ua}
            </p>
          )}
        </div>
      );
    }
    return (
      <p className="text-xs text-slate-500 italic" data-testid="detail-no-snapshot-never">
        Aucun snapshot reçu et jamais d&apos;ouverture du jeu détectée avec cette clé.
        L&apos;apprenant n&apos;a jamais lancé le jeu (ou joue avec une autre clé — vérifiez le bloc « Données non remontées »).
      </p>
    );
  }
  const updated = s.updated_at ? new Date(s.updated_at) : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
      <div className="bg-ink-800 border border-ink-600 rounded p-2.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Activité</p>
        <p className="text-slate-300">Phase {s.phase} · Dépôt phase {s.depot_phase}</p>
        <p className="text-slate-300">Année {s.year} · J{s.total_days}</p>
        {updated && (
          <p className="text-[10px] text-slate-500 mt-1">
            Dernier ping : {updated.toLocaleString('fr-FR')}
          </p>
        )}
      </div>
      <div className="bg-ink-800 border border-ink-600 rounded p-2.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Finances</p>
        <p className="text-slate-300">CA mensuel : {fmtMoney(s.monthly_revenue)}</p>
        <p className="text-slate-300">Charges : {fmtMoney(s.monthly_expenses)}</p>
        <p className={`font-mono ${(s.monthly_revenue - s.monthly_expenses) >= 0 ? 'text-money' : 'text-warn'}`}>
          Net : {fmtMoney((s.monthly_revenue || 0) - (s.monthly_expenses || 0))}
        </p>
      </div>
      <div className="bg-ink-800 border border-ink-600 rounded p-2.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Humain</p>
        <p className="text-slate-300">{s.drivers_total} chauffeur·s · sat. {s.drivers_avg_satisfaction}%</p>
        <p className="text-slate-300">{s.support_total} support · {s.contracts_active} contrats actifs</p>
        <p className="text-slate-300">Notoriété {s.notoriety}</p>
      </div>
      {Array.isArray(s.strategy_summary) && s.strategy_summary.length > 0 && (
        <div className="md:col-span-3 bg-ink-800 border border-purple-500/30 rounded p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-purple-300 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Analyse stratégique auto
          </p>
          <ul className="space-y-0.5">
            {s.strategy_summary.map((line) => (
              <li key={`gst-${line}`} className="text-slate-300 text-xs">• {line}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="md:col-span-3 bg-ink-800 border border-ink-600 rounded p-2.5">
        <SaveSlotsBlock
          saveSlots={s.save_slots}
          testIdPrefix={`gst-save-slots-${item.license?.license_key || 'unknown'}`}
        />
      </div>
    </div>
  );
}
